import React from 'react';
import {
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  FormHelperText,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import FormSectionCard from './FormSectionCard';
import { EnergyConsultationFormModel } from '../types';
import EnergyNeedCard from './EnergyNeedCard';
import { FormState } from '../../../hooks/useFormState.types';
import {
  BusinessNeedsCategory,
  CommercialRetailOfficeOption,
  LargeCommercialIndustrialOption,
} from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import HomeIcon from './icons/HomeIcon';
import BusinessIcon from './icons/BusinessIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
  }),
);

interface Props {
  form: FormState<EnergyConsultationFormModel>;
}

const BusinessEnergyNeedsSection: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const commercialRetailOfficeOptionLabels: Record<
    CommercialRetailOfficeOption,
    string
  > = {
    [CommercialRetailOfficeOption.EvChargers]: t(
      'COMMERCIAL_RETAIL_OFFICE_EV_CHARGERS',
    ),
    [CommercialRetailOfficeOption.LightingUpgrades]: t(
      'COMMERCIAL_RETAIL_OFFICE_LIGHTING_UPGRADES',
    ),
    [CommercialRetailOfficeOption.RenewablePower]: t(
      'COMMERCIAL_RETAIL_OFFICE_RENEWABLE_POWER',
    ),
    [CommercialRetailOfficeOption.SmartThermostat]: t(
      'COMMERCIAL_RETAIL_OFFICE_SMART_THERMOSTAT',
    ),
    [CommercialRetailOfficeOption.Other]: t('OTHER'),
  };

  const largeCommercialIndustrialOptionsLabels: Record<
    LargeCommercialIndustrialOption,
    string
  > = {
    [LargeCommercialIndustrialOption.AnalyticsTools]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_ANALYTICS_TOOLS',
    ),
    [LargeCommercialIndustrialOption.EnergyShifting]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_ENERGY_SHIFTING',
    ),
    [LargeCommercialIndustrialOption.EvChargers]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_EV_CHARGERS',
    ),
    [LargeCommercialIndustrialOption.OnsiteGenerationBattery]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_ONSITE_GENERATION_BATTERY',
    ),
    [LargeCommercialIndustrialOption.RenewablePower]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_RENEWABLE_POWER',
    ),
    [LargeCommercialIndustrialOption.SmartThermostat]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_SMART_THERMOSTAT',
    ),
    [LargeCommercialIndustrialOption.Other]: t('OTHER'),
  };

  const toggleCommercialRetailOfficeOption = (
    need: CommercialRetailOfficeOption,
  ) => {
    const exists = form.values?.commercialRetailOfficeOptions?.includes(need);

    if (exists) {
      void form.setValue(
        'commercialRetailOfficeOptions',
        form.values.commercialRetailOfficeOptions.filter(item => item !== need),
      );
    } else {
      void form.setValue('commercialRetailOfficeOptions', [
        ...form.values.commercialRetailOfficeOptions,
        need,
      ]);
    }
  };

  const toggleLargeCommercialIndustrialOptions = (
    need: LargeCommercialIndustrialOption,
  ) => {
    const exists = form.values?.largeCommercialIndustrialOptions?.includes(
      need,
    );

    if (exists) {
      void form.setValue(
        'largeCommercialIndustrialOptions',
        form.values.largeCommercialIndustrialOptions.filter(
          item => item !== need,
        ),
      );
    } else {
      void form.setValue('largeCommercialIndustrialOptions', [
        ...form.values.largeCommercialIndustrialOptions,
        need,
      ]);
    }
  };

  const selectedBusinessNeedsCategory = form.values.businessNeedsCategory;
  return (
    <FormSectionCard title={t('BUSINESS_ENERGY_NEEDS')}>
      <Box>
        <Typography className={classes.sectionTitle}>
          {t('WHAT_TYPE_OF_BUSINESS_DO_YOU_HAVE')}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} css={{ display: 'flex' }}>
          <EnergyNeedCard
            selected={
              selectedBusinessNeedsCategory ===
              BusinessNeedsCategory.CommercialRetailOffice
            }
            title={t('BUSINESS_ENERGY_NEED_COMMERCIAL_RETAIL_OFFICE')}
            description={t(
              'BUSINESS_ENERGY_NEED_COMMERCIAL_RETAIL_OFFICE_SUBTITLE',
            )}
            icon={<HomeIcon />}
            onSelect={() => {
              void form.setValue(
                'businessNeedsCategory',
                BusinessNeedsCategory.CommercialRetailOffice,
              );
              void form.setValue('largeCommercialIndustrialOptions', []);
            }}
          />
        </Grid>

        <Grid item xs={12} md={12} css={{ display: 'flex' }}>
          <EnergyNeedCard
            selected={
              selectedBusinessNeedsCategory ===
              BusinessNeedsCategory.LargeCommercialIndustrial
            }
            title={t('BUSINESS_ENERGY_NEED_LARGE_COMMERCIAL_INDUSTRIAL')}
            description={t(
              'BUSINESS_ENERGY_NEED_LARGE_COMMERCIAL_INDUSTRIAL_SUBTITLE',
            )}
            icon={<BusinessIcon />}
            onSelect={() => {
              void form.setValue(
                'businessNeedsCategory',
                BusinessNeedsCategory.LargeCommercialIndustrial,
              );
              void form.setValue('commercialRetailOfficeOptions', []);
            }}
          />
        </Grid>
      </Grid>

      {form.errors.businessNeedsCategory && (
        <FormHelperText error>
          {form.errors.businessNeedsCategory}
        </FormHelperText>
      )}

      {!selectedBusinessNeedsCategory && (
        <Box mt={1}>
          <Typography variant="subtitle1">
            {t('SELECT_BUSINESS_ENERGY_NEEDS')}
          </Typography>
        </Box>
      )}

      {selectedBusinessNeedsCategory && (
        <>
          <Box mt={1} p={2} display="flex" flexDirection="column">
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 600,
              }}
            >
              {t('WHAT_ENERGY_SOLUTION_YOU_ARE_INTERESTED_IN')}
            </Typography>

            <Typography
              variant="subtitle2"
              style={{
                fontSize: 12,
                fontWeight: 'normal',
              }}
            >
              {t('SELECT_ALL_THAT_APPLY')}
            </Typography>

            {selectedBusinessNeedsCategory ===
              BusinessNeedsCategory.CommercialRetailOffice &&
              (Object.keys(
                commercialRetailOfficeOptionLabels,
              ) as CommercialRetailOfficeOption[]).map(option => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={form.values?.commercialRetailOfficeOptions?.includes(
                        option,
                      )}
                      onChange={() =>
                        toggleCommercialRetailOfficeOption(option)
                      }
                      color="primary"
                    />
                  }
                  label={commercialRetailOfficeOptionLabels[option]}
                />
              ))}

            {selectedBusinessNeedsCategory ===
              BusinessNeedsCategory.LargeCommercialIndustrial &&
              (Object.keys(
                largeCommercialIndustrialOptionsLabels,
              ) as LargeCommercialIndustrialOption[]).map(option => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={form.values?.largeCommercialIndustrialOptions?.includes(
                        option,
                      )}
                      onChange={() =>
                        toggleLargeCommercialIndustrialOptions(option)
                      }
                      color="primary"
                    />
                  }
                  label={largeCommercialIndustrialOptionsLabels[option]}
                />
              ))}

            {(form.values?.commercialRetailOfficeOptions?.includes(
              CommercialRetailOfficeOption.Other,
            ) ||
              form.values?.largeCommercialIndustrialOptions?.includes(
                LargeCommercialIndustrialOption.Other,
              )) && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t('PLEASE_SPECIFY')}
                  data-testid="other-business-need"
                  {...form.props('otherBusinessNeed')}
                />
              </Box>
            )}
          </Box>
          <Typography
            variant="subtitle2"
            style={{
              marginBottom: 12,
              fontSize: '12px',
              fontWeight: 'normal',
            }}
          >
            {t('SELECTIONS_HELP_ENERGY_EXPERT')}
          </Typography>
          {(form.errors.commercialRetailOfficeOptions ||
            form.errors.largeCommercialIndustrialOptions) && (
            <FormHelperText error>
              {form.errors.commercialRetailOfficeOptions ||
                form.errors.largeCommercialIndustrialOptions}
            </FormHelperText>
          )}
        </>
      )}
    </FormSectionCard>
  );
};

export default BusinessEnergyNeedsSection;
